import { wordKey, wordsOf } from "./wordKey";

const SCORE = {
  EXACT_WORD: 300,
  DISPLAY_PREFIX: 250,
  WORD_PREFIX: 200,
  SUBSTRING: 100,
  RARITY_WEIGHT: 3,
  POSITION_PENALTY: 8,
  EXTRA_LETTER_PENALTY: 2,
};
const PHRASE_OPENS_TERM_BONUS = 2;
const PHRASE_LEFTOVER_WORD_PENALTY = 0.25;
const DEFAULT_PHRASE_CAP = 8;

const lineUp = (tail, displayWords, first) =>
  tail.every(({ key }, i) =>
    i < tail.length - 1
      ? key === displayWords[first + i]
      : key !== "" && displayWords[first + i].startsWith(key)
  );

// Longest run of trailing typed words that matches a run of the term's words (the last one as a
// prefix); `first` is where in the term the run starts. null under two words: one word is
// single-word matching's job.
export function phraseAlignment(text, display) {
  const displayWords = wordsOf(display);
  const typed = [...text.matchAll(/\S+/g)].map((m) => ({ key: wordKey(m[0]), start: m.index }));
  const longest = Math.min(typed.length, displayWords.length);
  const hit = Array.from({ length: Math.max(longest - 1, 0) }, (_, i) => longest - i)
    .flatMap((count) =>
      Array.from({ length: displayWords.length - count + 1 }, (_, first) => ({ count, first }))
    )
    .find(({ count, first }) => lineUp(typed.slice(-count), displayWords, first));
  return hit ? { start: typed.slice(-hit.count)[0].start, ...hit } : null;
}

const buildStats = (termIndex = []) => {
  const displays = [...new Set(termIndex.map((t) => (t.display || "").toLowerCase()).filter(Boolean))];
  const termsWithWord = displays
    .flatMap((display) => [...new Set(wordsOf(display))])
    .reduce((counts, word) => counts.set(word, (counts.get(word) ?? 0) + 1), new Map());
  const total = Math.max(displays.length, 1);
  // +1 so a word in every term still counts for something.
  return { idf: (word) => Math.log(total / (termsWithWord.get(wordKey(word)) ?? 1)) + 1 };
};

// Rare words say more than common ones ("gun" vs "score"); cached per term index.
const statsCache = new WeakMap();
export const wordStats = (termIndex) =>
  termIndex ? statsCache.get(termIndex) ?? statsCache.set(termIndex, buildStats(termIndex)).get(termIndex) : buildStats();

const phraseScore = (display, { first, count }, stats) =>
  wordsOf(display)
    .slice(first, first + count)
    .reduce((sum, word) => sum + stats.idf(word), 0) +
  (first === 0 ? PHRASE_OPENS_TERM_BONUS : 0) +
  count -
  (wordsOf(display).length - count) * PHRASE_LEFTOVER_WORD_PENALTY;

// Terms the typed words read as a phrase, most relevant first.
export const phraseMatches = (text, termIndex, { cap = DEFAULT_PHRASE_CAP, stats = wordStats(termIndex) } = {}) =>
  (termIndex ?? [])
    .map((term) => ({ term, hit: phraseAlignment(text, term.display || "") }))
    .filter(({ hit }) => hit)
    .map(({ term, hit }) => ({ term, ...hit, score: phraseScore(term.display, hit, stats) }))
    .sort((a, b) => b.score - a.score)
    .slice(0, cap);

// Relevance of a term to one typed word: exact word > prefix, early word > late, short completion
// > long, rare word > common.
export const singleWordScore = (fragment, term, stats) => {
  const needle = fragment.trim().toLowerCase();
  const display = (term.display || "").toLowerCase();
  const wordScore = (word, i) =>
    word.startsWith(needle)
      ? (word === needle ? SCORE.EXACT_WORD : SCORE.WORD_PREFIX) +
        stats.idf(word) * SCORE.RARITY_WEIGHT -
        i * SCORE.POSITION_PENALTY -
        (word.length - needle.length) * SCORE.EXTRA_LETTER_PENALTY
      : 0;
  return !needle || !display
    ? 0
    : Math.max(
        display.includes(needle) ? SCORE.SUBSTRING : 0,
        display.startsWith(needle) ? SCORE.DISPLAY_PREFIX : 0,
        ...display.split(/\s+/).map(wordScore)
      );
};
